import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert } from "lucide-react";

export default function Profile() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({ name: "", phone: "", marketing_opt_in: false });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [busy, setBusy] = useState(false);
  const [pwErr, setPwErr] = useState("");

  useEffect(() => {
    if (user) setForm({ name: user.name || "", phone: user.phone || "", marketing_opt_in: !!user.marketing_opt_in });
  }, [user]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.patch("/auth/profile", form);
      setUser(res.data);
      toast.success("Profile saved");
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const changePw = async (e) => {
    e.preventDefault();
    setPwErr("");
    try {
      await api.post("/auth/change-password", pw);
      toast.success("Password updated");
      setPw({ current_password: "", new_password: "" });
    } catch (e) { setPwErr(formatApiError(e)); }
  };

  const resendVerify = async () => {
    try {
      const r = await api.post("/auth/resend-verification");
      if (r.data.already_verified) toast.info("Already verified.");
      else toast.success("Verification email sent" + (r.data.verification_link ? " (check dev console)" : ""));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="profile-page" className="space-y-6">
      <div>
        <div className="eyebrow mb-2">Account Settings</div>
        <h1 className="font-display text-3xl font-semibold text-slate-900 tracking-tight">Profile.</h1>
      </div>

      <div className="border border-slate-200 rounded-md bg-white p-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="eyebrow">Email Verification</div>
          {user?.email_verified
            ? <Badge className="bg-blue-50 text-blue-700 border-blue-200 rounded-md font-mono text-[10px] uppercase tracking-widest"><ShieldCheck className="w-3 h-3 mr-1" strokeWidth={2}/> Verified</Badge>
            : <Badge className="bg-amber-50 text-amber-700 border-amber-200 rounded-md font-mono text-[10px] uppercase tracking-widest"><ShieldAlert className="w-3 h-3 mr-1" strokeWidth={2}/> Unverified</Badge>}
        </div>
        <div className="text-sm text-slate-600">{user?.email}</div>
        {!user?.email_verified && (
          <Button onClick={resendVerify} className="mt-4 bg-slate-900 hover:bg-blue-600 text-white rounded-md" data-testid="resend-verify-btn">Resend verification email</Button>
        )}
      </div>

      <form onSubmit={save} className="border border-slate-200 rounded-md bg-white p-6 space-y-3" data-testid="profile-form">
        <div className="eyebrow mb-2">Personal Info</div>
        <div className="space-y-2">
          <label className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Name</label>
          <Input data-testid="profile-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="rounded-md border-slate-300"/>
        </div>
        <div className="space-y-2">
          <label className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Phone (optional)</label>
          <Input data-testid="profile-phone" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="rounded-md border-slate-300"/>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={form.marketing_opt_in} onChange={(e) => setForm({...form, marketing_opt_in: e.target.checked})}/>
          Email me new-batch announcements and research updates
        </label>
        <Button type="submit" disabled={busy} className="bg-blue-600 hover:bg-blue-700 text-white rounded-md" data-testid="profile-save">
          {busy ? "Saving…" : "Save changes"}
        </Button>
      </form>

      <form onSubmit={changePw} className="border border-slate-200 rounded-md bg-white p-6 space-y-3">
        <div className="eyebrow mb-2">Change Password</div>
        <Input type="password" required placeholder="Current password" value={pw.current_password} onChange={(e) => setPw({...pw, current_password: e.target.value})} className="rounded-md border-slate-300"/>
        <Input type="password" required minLength={8} placeholder="New password (min 8)" value={pw.new_password} onChange={(e) => setPw({...pw, new_password: e.target.value})} className="rounded-md border-slate-300"/>
        {pwErr && <div className="text-xs text-red-600 border border-red-200 bg-red-50 rounded-md p-2">{pwErr}</div>}
        <Button type="submit" variant="outline" className="border-slate-300 rounded-md">Update password</Button>
      </form>
    </div>
  );
}
